import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import Button from '@/components/Button';
import { FormLayout, Larger } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import Text from '@/components/Text';
import useAccountNumbersOptions from '@/hooks/useAccountNumbersOptions';
import useAlerts from '@/hooks/useAlerts';
import useDepartmentsOptions from '@/hooks/useDepartmentsOptions';
import GpmCheckboxFiekd from '@/pages/AddInvoice/components/GpmCheckboxFiekd';
import { FreelancerInvoiceSubmissionResource } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

import InvoiceUpdateModalActions from './InvoiceUpdateModalActions';

type FormDataT = {
  department_id: number | null;
  finance_account_number_id: number | null;
  gpm: boolean;
  team_lead_comment: string;
  rejection_reason: string;
  approvement_comment: string;
};

const CommentBox = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
`;

interface Props {
  submission: FreelancerInvoiceSubmissionResource;
  queryKey: string;
  onClose: () => void;
}

const InvoiceUpdateModal: FC<Props> = ({ submission, queryKey, onClose }) => {
  const alerts = useAlerts();
  const queryClient = useQueryClient();
  const [selectedAction, setSelectedAction] = useState<
    'teamLeadActions' | 'returnToFreelancer' | 'rejectPayment' | null
  >(null);

  const { data: departmentOptions = [] } = useDepartmentsOptions();
  const { data: accountNumberOptions = [] } = useAccountNumbersOptions();

  const filteredDepartmentOptions = departmentOptions.filter(
    (d) => !d.hide_from_dropdowns,
  );

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      department_id: submission.departmentId ?? null,
      finance_account_number_id: submission.financeAccountNumberId ?? null,
      gpm: submission.gpm ?? false,
      approvement_comment: submission.approvementComment ?? '',
    },
  });

  const isGpm = useWatch({ control, name: 'gpm' });

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (selectedAction === 'returnToFreelancer') {
        await api.post(
          `/v1/payments/freelancer-invoice-submissions/${submission.id}/request-changes`,
          { team_lead_comment: data.team_lead_comment },
        );
        alerts.success('Submission returned to freelancer');
      } else if (selectedAction === 'rejectPayment') {
        await api.post(
          `/v1/payments/freelancer-invoice-submissions/${submission.id}/reject`,
          { rejection_reason: data.rejection_reason },
        );
        alerts.success('Submission rejected successfully');
      } else {
        await api.post(
          `/v1/payments/freelancer-invoice-submissions/${submission.id}/approve`,
          data,
        );
        alerts.success('Submission approved successfully');
      }
      await queryClient.invalidateQueries([queryKey], { exact: false });
      onClose();
    } catch (err) {
      handleFormErrors<FormDataT>(err, setError, alerts);
    }
  });

  return (
    <Modal>
      <SidePanel heading="Finances Rejected" onClose={onClose}>
        <SidePanelBody>
          <SidePanelContent>
            <FormLayout>
              <Larger>
                {submission.financesComment && (
                  <CommentBox>
                    <Text $color="gray" $type="smallHeadline">
                      Comment
                    </Text>
                    <Text $type="button">{submission.financesComment}</Text>
                  </CommentBox>
                )}
              </Larger>
            </FormLayout>
            <InvoiceUpdateModalActions
              selectedAction={selectedAction}
              onSelect={setSelectedAction}
            />
            {selectedAction === 'returnToFreelancer' && (
              <FormLayout>
                <FormField<FormDataT>
                  control={control}
                  name="team_lead_comment"
                  label="Comment"
                  type="textarea"
                  placeholder="Add comment"
                />
              </FormLayout>
            )}
            {selectedAction === 'rejectPayment' && (
              <FormLayout>
                <FormField<FormDataT>
                  control={control}
                  name="rejection_reason"
                  label="Comment"
                  type="textarea"
                  placeholder="Add comment"
                />
              </FormLayout>
            )}
            {selectedAction === 'teamLeadActions' && (
              <FormLayout>
                <FormField<FormDataT, number>
                  control={control}
                  name="department_id"
                  label="Department"
                  type="select"
                  options={filteredDepartmentOptions}
                />
                <FormField<FormDataT, number>
                  control={control}
                  name="finance_account_number_id"
                  label="Finance Account Number"
                  type="select"
                  options={accountNumberOptions}
                />
                {submission.hours != null && (
                  <GpmCheckboxFiekd isGpm={isGpm} setValue={setValue} />
                )}
                <FormField<FormDataT>
                  control={control}
                  name="approvement_comment"
                  label="Approvement Comment"
                  type="textarea"
                  placeholder="Add comment"
                />
              </FormLayout>
            )}
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              type="submit"
              disabled={isSubmitting || selectedAction === null}
              loading={isSubmitting}
              onClick={onSubmit}
            >
              Submit
            </Button>
            <Button color="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default InvoiceUpdateModal;
